import React from 'react';
import {Box, Chip, ClickAwayListener, IconButton, TextField, Tooltip, Typography,} from '@mui/material';
import {Cancel as CancelIcon, Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Unarchive as UnarchiveIcon,} from '@mui/icons-material';
import {TaskWithImages} from '../hooks/useTaskDetail';

interface TaskHeaderProps {
    taskId: number;
    task: TaskWithImages | null;
    editMode: boolean;
    editingField: string | null;
    editValues: { [key: string]: any };
    hasUnsavedChanges: boolean;
    isTaskArchived: boolean;
    submitting?: boolean;
    getCurrentValue: (field: string) => any;
    handleClose: () => void;
    handleStartEdit: () => void;
    handleSave?: () => void;
    handleCancelEdit?: () => void;
    setEditingField: (field: string | null) => void;
    setEditValues: (values: { [key: string]: any }) => void;
    handleTitleSave: () => void;
    handleKeyDown: (e: React.KeyboardEvent, saveHandler: () => void) => void;
    setOpenEndTask: (open: boolean) => void;
    handleArchiveTask?: () => void;
    handleRestoreTask?: () => void;
    canArchive?: boolean;
    sessionUserId?: number | null;
    userRole?: string[] | null;
}

export const TaskHeader: React.FC<TaskHeaderProps> = ({
                                                          taskId,
                                                          task,
                                                          editMode,
                                                          editingField,
                                                          editValues,
                                                          hasUnsavedChanges,
                                                          isTaskArchived,
                                                          submitting = false,
                                                          getCurrentValue,
                                                          handleClose,
                                                          handleStartEdit,
                                                          handleSave,
                                                          handleCancelEdit,
                                                          setEditingField,
                                                          setEditValues,
                                                          handleTitleSave,
                                                          handleKeyDown,
                                                          setOpenEndTask,
                                                          handleArchiveTask,
                                                          handleRestoreTask,
                                                          canArchive = false,
                                                          sessionUserId,
                                                      }) => {
    return (
        <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box display='flex' alignItems='center' gap={1}>
                <Typography variant='h5' component='div'>
                    Aufgabe #{taskId}
                </Typography>
            </Box>
            <Box display='flex' alignItems='center' gap={1}>
                {editMode ? (
                    /* Edit Mode Buttons */
                    <>
                        <Tooltip title="Änderungen verwerfen">
                            <IconButton
                                onClick={handleCancelEdit}
                                className="task-header-button task-header-cancel-button"
                                color='secondary'
                            >
                                <CancelIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={hasUnsavedChanges ? "Änderungen speichern" : "Keine Änderungen vorhanden"}>
              <span>
                <IconButton
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges || submitting}
                    className={`task-header-button task-header-save-button ${hasUnsavedChanges ? 'enabled' : 'disabled'}`}
                    color='primary'
                >
                  <SaveIcon/>
                </IconButton>
              </span>
                        </Tooltip>
                    </>
                ) : (
                    /* Normal Mode Buttons */
                    <>
                        {!isTaskArchived && canArchive && (
                            <Tooltip title="Bearbeitung starten">
                                <IconButton
                                    onClick={handleStartEdit}
                                    className="task-header-button task-header-edit-button"
                                >
                                    <EditIcon/>
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Archivieren/Wiederherstellen Button */}
                        {canArchive && (
                            <Tooltip title={isTaskArchived ? "Wiederherstellen" : "Löschen"}>
                                <IconButton
                                    onClick={isTaskArchived ? handleRestoreTask : handleArchiveTask}
                                    className="task-header-button task-header-archive-button"
                                    sx={{
                                        color: isTaskArchived ? 'success.main' : 'error.main',
                                        '&:hover': {
                                            backgroundColor: isTaskArchived ? 'success.light' : 'error.light',
                                            color: isTaskArchived ? 'success.dark' : 'error.dark'
                                        }
                                    }}
                                >
                                    {isTaskArchived ? <UnarchiveIcon/> : <DeleteIcon/>}
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                )}

                <Tooltip title="Schließen">
                    <IconButton
                        onClick={handleClose}
                        className="task-header-button task-header-close-button"
                    >
                        <CloseIcon/>
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

interface TaskTitleProps {
    task: TaskWithImages | null;
    editMode: boolean;
    editingField: string | null;
    editValues: { [key: string]: any };
    hasUnsavedChanges: boolean;
    isTaskArchived: boolean;
    getCurrentValue: (field: string) => any;
    setEditingField: (field: string | null) => void;
    setEditValues: (values: { [key: string]: any }) => void;
    handleTitleSave: () => void;
    handleKeyDown: (e: React.KeyboardEvent, saveHandler: () => void) => void;
}

export const TaskTitle: React.FC<TaskTitleProps> = ({
                                                        task,
                                                        editMode,
                                                        editingField,
                                                        editValues,
                                                        hasUnsavedChanges,
                                                        isTaskArchived,
                                                        getCurrentValue,
                                                        setEditingField,
                                                        setEditValues,
                                                        handleTitleSave,
                                                        handleKeyDown,
                                                    }) => {
    if (editingField === 'title') {
        return (
            <ClickAwayListener onClickAway={handleTitleSave}>
                <TextField
                    fullWidth
                    autoFocus
                    value={editValues.title ?? task?.title ?? ''}
                    onChange={(e) => setEditValues({...editValues, title: e.target.value})}
                    onKeyDown={(e) => handleKeyDown(e, handleTitleSave)}
                    variant='outlined'
                    size='medium'
                    sx={{
                        '& .MuiInputBase-input': {
                            fontSize: '2rem',
                            fontWeight: 500,
                        },
                    }}
                />
            </ClickAwayListener>
        );
    }

    return (
        <Box
            onClick={() => {
                if (!isTaskArchived && editMode) {
                    setEditingField('title');
                    setEditValues({title: getCurrentValue('title')});
                }
            }}
            sx={{
                cursor: isTaskArchived || !editMode ? 'default' : 'pointer',
                padding: 1,
                borderRadius: 1,
                '&:hover': {
                    backgroundColor: isTaskArchived || !editMode ? 'transparent' : 'action.hover',
                },
            }}>
            <Typography variant='h4' component='h1' sx={{fontWeight: 500}}>
                {getCurrentValue('title') || 'Ohne Titel'}
                {hasUnsavedChanges && (
                    <Chip
                        label='Ungespeicherte Änderungen'
                        color='warning'
                        size='small'
                        sx={{ml: 2, verticalAlign: 'middle'}}
                    />
                )}
                {isTaskArchived && (
                    <Chip
                        label='Archiviert'
                        color='default'
                        size='small'
                        sx={{ml: 2, verticalAlign: 'middle'}}
                    />
                )}
            </Typography>
        </Box>
    );
};
